/**
 * <burnout-videos>
 *
 * Two jobs:
 *
 *  1. Play the clips that are actually on screen, muted, and pause the rest.
 *     Autoplaying every video in a rail wastes bandwidth and, on a phone,
 *     battery — so playback follows visibility.
 *
 *  2. Open a tapped clip full size with sound, in a native <dialog> so focus
 *     trapping and Esc come from the platform.
 *
 * The markup preloads metadata only, so a first frame is ready without pulling
 * whole clips down; the visible cover image is a real <img> the CSS fades out
 * once playback actually starts.
 */
class BurnoutVideos extends HTMLElement {
  #dialog = null;
  #dialogVideo = null;
  #inline = [];

  connectedCallback() {
    this.#dialog = this.querySelector('.bo-vid__modal');
    this.#dialogVideo = this.querySelector('.bo-vid__modal-player');
    this.#inline = Array.from(this.querySelectorAll('.bo-vid__player'));

    this.querySelectorAll('[data-bo-video-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.#open(trigger.dataset.videoSrc));
    });

    this.querySelectorAll('[data-bo-video-close]').forEach((el) =>
      el.addEventListener('click', this.#close)
    );

    if (this.#dialog) {
      this.#dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.#close();
      });
      // Clicking the backdrop closes it.
      this.#dialog.addEventListener('click', (event) => {
        if (event.target === this.#dialog) this.#close();
      });
    }

    this.#watchVisibility();
    this.#revealOnPlay();
  }

  /**
   * Muted inline playback, driven by visibility.
   *
   * A save-data or reduced-motion preference leaves every clip paused on its
   * poster frame — the cards still read as videos and still open on tap.
   */
  #watchVisibility() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (navigator.connection?.saveData) return;

    /*
     * No autoplay on a touch device.
     *
     * These clips are 5-11MB each. Autoplaying them as the rail scrolls past
     * meant several multi-megabyte streams downloading at once, and anything
     * the visitor tapped next — a product, a collection — had to queue behind
     * them. That was the difference between a page opening in a second and
     * appearing to hang.
     *
     * The cards still look and behave like videos: the poster is always
     * showing, and a tap opens the clip full size with sound, which is what
     * most people do on a phone anyway.
     */
    if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;

    // A slow connection gets the same treatment regardless of device.
    if (/(^|-)(2g|3g)$/.test(navigator.connection?.effectiveType || '')) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;
          if (entry.isIntersecting) {
            // play() rejects if the browser blocks it; there is nothing useful
            // to do about that, and an unhandled rejection is noise.
            video.play().catch(() => {});
          } else {
            video.pause();
            /*
             * Pausing stops playback but the browser carries on buffering the
             * rest of the file. Dropping the source aborts the transfer, which
             * is the point of doing this at all; the poster layer stays put, so
             * the card looks unchanged and the clip reloads on demand.
             */
            const source = video.querySelector('source');
            if (source && video.readyState > 0) {
              video.removeAttribute('src');
              video.load();
            }
          }
        });
      },
      { threshold: 0.55 }
    );

    this.#inline.forEach((video) => {
      if (video instanceof HTMLVideoElement) observer.observe(video);
    });
  }

  /**
   * Reveal each clip only once it is genuinely painting frames.
   *
   * The cards show a real <img> over the video rather than relying on the
   * poster attribute, because the browser discards that attribute the moment
   * play() is called — which left the card black for as long as the first
   * chunk took to arrive. The image stays put until the video says it is
   * playing, so there is never a gap with nothing in it.
   *
   * `playing` is the honest signal; `loadeddata` fires when a frame exists but
   * before it is necessarily on screen. Once revealed it stays revealed —
   * pausing keeps the last frame, so re-hiding would flash the poster back in
   * every time a card scrolled out of view.
   */
  #revealOnPlay() {
    this.#inline.forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;

      const stage = video.closest('.bo-vid__stage');
      if (!stage || !stage.querySelector('.bo-vid__poster')) return;

      const reveal = () => stage.classList.add('is-playing');

      video.addEventListener('playing', reveal, { once: true });

      // A clip already buffered from the bfcache may never fire `playing`.
      if (video.readyState >= 3 && !video.paused) reveal();
    });
  }

  #open = (src) => {
    if (!this.#dialog || !this.#dialogVideo || !src) return;

    // Silence the rail so two soundtracks never overlap.
    this.#inline.forEach((v) => {
      if (v instanceof HTMLVideoElement) v.pause();
    });

    this.#dialogVideo.src = src;
    this.#dialogVideo.currentTime = 0;
    this.#dialog.showModal();
    document.body.classList.add('bo-vid-open');
    this.#dialogVideo.play().catch(() => {});
  };

  #close = () => {
    if (!this.#dialog?.open) return;
    this.#dialogVideo.pause();
    // Clearing the source stops the download continuing behind a closed modal.
    this.#dialogVideo.removeAttribute('src');
    this.#dialogVideo.load();
    this.#dialog.close();
    document.body.classList.remove('bo-vid-open');
  };
}

if (!customElements.get('burnout-videos')) {
  customElements.define('burnout-videos', BurnoutVideos);
}
